import { inject, Injectable } from '@angular/core';

import { JourneyAnalyticsLoader } from './journey-analytics-loader.service';

type InstanceRef = NonNullable<
  ReturnType<JourneyAnalyticsLoader['getInstance']>
>;

type ClientRef = ReturnType<InstanceRef['client']>;

/**
 * Returns the instantiated analytics service.
 * Either the Rudderstack SDK or a stub, based on environment configuration.
 *
 * This facade is meant to control what methods to expose from the underlying Charon SDK,
 * consider it the public API for all consuming teams.
 */
@Injectable({ providedIn: 'root' })
export class JourneyAnalytics {
  readonly #loader = inject(JourneyAnalyticsLoader);
  readonly #analytics = this.#loader.getInstance();

  #verifyProvider() {
    if (this.#analytics === null) {
      throw new Error(
        `No analytics instance is available yet. This means 'provideJourneyAnalytics' has not been provided in your application. 
      Please add this provider to your main module / component before using this service.`
      );
    }
  }

  track(...args: Parameters<InstanceRef['track']>) {
    this.#verifyProvider();
    return this.#analytics.track(...args);
  }

  identify(...args: Parameters<ClientRef['identify']>) {
    this.#verifyProvider();
    return this.#analytics.client().identify(...args);
  }

  page(...args: Parameters<ClientRef['page']>) {
    this.#verifyProvider();
    return this.#analytics.client().page(...args);
  }
}
